/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, Text, Radio, Stack, useStyleConfig} from '@chakra-ui/react'
import {
    ADD_FILTER,
    REMOVE_FILTER,
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {remCalc} from '../../../theme/_utils'

const RadioRefinement = ({filter, value, toggleFilter, isSelected}) => {
    const styles = useStyleConfig('RadioRefinements')
    const buttonRef = useRef()
    const {formatMessage} = useIntl()
    // Because choosing a refinement is equivalent to a form submission, the best semantic choice
    // for the refinement is a button or a link, rather than a radio input. The radio element here
    // is purely for visual purposes, and should probably be replaced with a simple icon.
    return (
        <Box {...styles?.stackItem}>
            <Box
                {...styles?.textContainer}
                data-testid="text-container"
                onClick={() => buttonRef.current?.click()}
            >
                <Text
                    ref={buttonRef}
                    as="button"
                    fontSize="sm"
                    onClick={() =>
                        toggleFilter(value, filter.attributeId, filter.label, false, false)
                    }
                    aria-label={formatMessage(isSelected ? REMOVE_FILTER : ADD_FILTER, value)}
                >
                    {value.label}
                </Text>
                <Text {...styles?.filterHit} aria-hidden="false">
                    {/* avoid redundant readout since swatch has aria label */}
                    {formatMessage(
                        {
                            id: 'radioRefinements.hitCount',
                            defaultMessage: '({radioRefinementsHitCount})',
                        },
                        {radioRefinementsHitCount: value.hitCount},
                    )}
                </Text>
            </Box>
            <Radio
                display="inline-flex"
                height={{base: '44px', lg: '24px'}}
                borderWidth={remCalc(0.6)}
                isChecked={isSelected}
                // Ideally, this "icon" would be part of the button, but doing so with a radio input
                // triggers `onClick` twice. The radio must be separate, and therefore we must add
                // these workarounds to prevent it from receiving focus.
                inputProps={{'aria-hidden': true, tabIndex: -1}}
                onClick={() => buttonRef.current?.click()}
            />
        </Box>
    )
}

RadioRefinement.propTypes = {
    filter: PropTypes.object,
    value: PropTypes.object,
    toggleFilter: PropTypes.func,
    isSelected: PropTypes.bool,
}

const RadioRefinements = ({filter, toggleFilter, selectedFilters}) => {
    const styles = useStyleConfig('RadioRefinements')
    return (
        <Stack {...styles?.stack}>
            {filter.values.map((value) => {
                const isSelected = selectedFilters.includes(value.value)
                // Don't display refinements with no results, unless we got there by selecting too
                // many refinements
                if (value.hitCount === 0 && !isSelected) return
                return (
                    <RadioRefinement
                        key={value.value}
                        value={value}
                        filter={filter}
                        toggleFilter={toggleFilter}
                        isSelected={isSelected}
                    />
                )
            })}
        </Stack>
    )
}

RadioRefinements.propTypes = {
    filter: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.arrayOf(PropTypes.object),
}

export default RadioRefinements
